import { useEffect, useId, useMemo, useRef, useState } from 'react'

import type { Dialekt } from '@/lib/api'

/**
 * Auswahl mit Texteingabe (ARIA-Combobox).
 *
 * Bewusst kein `<select>`: bei zehn und mehr Dialekten will man tippen können.
 * Und bewusst kein `<datalist>` — dessen Darstellung liegt beim Browser, sie
 * lässt sich weder gestalten noch verlässlich mit der Tastatur bedienen.
 *
 * Gefiltert wird umlaut- und akzentunabhängig: „sachsisch" findet „Sächsisch",
 * denn genau so tippt jemand, der die Tastatur nicht umstellen will.
 */

function faltung(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ß/g, 'ss')
    .toLowerCase()
}

function passt(dialekt: Dialekt, suche: string): boolean {
  if (!suche.trim()) return true
  const nadel = faltung(suche)
  return [dialekt.name, dialekt.region, dialekt.tagline].some((feld) =>
    faltung(feld).includes(nadel),
  )
}

interface Props {
  dialekte: Dialekt[]
  gewaehlt: Dialekt | null
  onWaehlen: (dialekt: Dialekt) => void
}

export function DialektWahl({ dialekte, gewaehlt, onWaehlen }: Props) {
  const [offen, setOffen] = useState(false)
  const [suche, setSuche] = useState('')
  const [markiert, setMarkiert] = useState(0)
  const huelle = useRef<HTMLDivElement>(null)
  const feld = useRef<HTMLInputElement>(null)
  const listenId = useId()

  const treffer = useMemo(
    () => dialekte.filter((dialekt) => passt(dialekt, suche)),
    [dialekte, suche],
  )

  // Ein Klick daneben schließt die Liste — sonst bliebe sie über der Seite
  // stehen, während man schon woanders tippt.
  useEffect(() => {
    if (!offen) return
    const beiKlick = (ereignis: MouseEvent) => {
      if (!huelle.current?.contains(ereignis.target as Node)) setOffen(false)
    }
    document.addEventListener('mousedown', beiKlick)
    return () => {
      document.removeEventListener('mousedown', beiKlick)
    }
  }, [offen])

  function oeffnen() {
    setOffen(true)
    setSuche('')
    setMarkiert(Math.max(0, dialekte.findIndex((d) => d.id === gewaehlt?.id)))
  }

  function waehlen(dialekt: Dialekt) {
    onWaehlen(dialekt)
    setOffen(false)
    setSuche('')
    feld.current?.blur()
  }

  function beiTaste(ereignis: React.KeyboardEvent<HTMLInputElement>) {
    if (ereignis.key === 'ArrowDown' || ereignis.key === 'ArrowUp') {
      ereignis.preventDefault()
      if (!offen) {
        oeffnen()
        return
      }
      const richtung = ereignis.key === 'ArrowDown' ? 1 : -1
      setMarkiert((bisher) => {
        if (treffer.length === 0) return 0
        return (bisher + richtung + treffer.length) % treffer.length
      })
      return
    }
    if (ereignis.key === 'Enter' && offen) {
      ereignis.preventDefault()
      const ziel = treffer[markiert]
      if (ziel) waehlen(ziel)
      return
    }
    if (ereignis.key === 'Escape' && offen) {
      ereignis.preventDefault()
      setOffen(false)
      setSuche('')
    }
  }

  return (
    <div ref={huelle} className="relative">
      <label htmlFor={`${listenId}-feld`} className="text-on-surface-muted mb-1 block text-xs">
        Dialekt
      </label>
      <input
        id={`${listenId}-feld`}
        ref={feld}
        role="combobox"
        aria-expanded={offen}
        aria-controls={`${listenId}-liste`}
        aria-autocomplete="list"
        aria-activedescendant={offen && treffer[markiert] ? `${listenId}-${treffer[markiert].id}` : undefined}
        autoComplete="off"
        value={offen ? suche : (gewaehlt?.name ?? '')}
        placeholder={gewaehlt ? gewaehlt.name : 'Dialekt suchen…'}
        onFocus={oeffnen}
        onChange={(ereignis) => {
          setSuche(ereignis.target.value)
          setMarkiert(0)
          setOffen(true)
        }}
        onKeyDown={beiTaste}
        className="border-outline rounded-pill bg-surface-container focus-visible:border-primary w-full border px-4 py-2.5 text-base transition-colors"
      />

      {offen && (
        <ul
          id={`${listenId}-liste`}
          role="listbox"
          aria-label="Dialekte"
          className="bg-surface-container border-outline rounded-card animate-rise absolute z-10 mt-2 max-h-72 w-full overflow-y-auto border py-1 shadow-lg"
        >
          {treffer.length === 0 && (
            <li className="text-on-surface-muted px-4 py-3 text-sm">
              Kein Dialekt passt zu „{suche}“. Probier den Ort oder die Region.
            </li>
          )}
          {treffer.map((dialekt, index) => (
            <li
              key={dialekt.id}
              id={`${listenId}-${dialekt.id}`}
              role="option"
              aria-selected={dialekt.id === gewaehlt?.id}
              onMouseEnter={() => {
                setMarkiert(index)
              }}
              onMouseDown={(ereignis) => {
                // mousedown, nicht click: sonst schlösse der Blur die Liste,
                // bevor die Auswahl ankommt.
                ereignis.preventDefault()
                waehlen(dialekt)
              }}
              className={`cursor-pointer px-4 py-2 ${index === markiert ? 'bg-surface' : ''}`}
            >
              <span className="flex items-baseline justify-between gap-3">
                <span className="font-medium">{dialekt.name}</span>
                <span
                  aria-hidden="true"
                  className="h-3 w-3 shrink-0 rounded-full"
                  style={{ background: dialekt.theme.primary }}
                />
              </span>
              <span className="text-on-surface-muted block text-xs">{dialekt.region}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
